import { AI_PICK_BATTERY_KWH } from "./batteryOptions";
import { DEFAULT_INPUTS } from "./examplePlanModel";

/** A priced active system option, trimmed to what the homepage example needs. */
export interface ExampleSystemOption {
  id: number;
  panels: number;
  batteryKwh: number;
  cost: number;
  /** Year-1 benefit (`savings` on the system option). */
  annualBenefit: number;
  systemOutputW: number | null;
  displayOrder: number | null;
}

function numberOrNull(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Keep active rows that have a panel count and a positive installed price. */
export function pricedExampleOptions(payload: unknown): ExampleSystemOption[] {
  if (!Array.isArray(payload)) return [];

  const options: ExampleSystemOption[] = [];
  for (const row of payload) {
    if (!row || typeof row !== "object") continue;
    const record = row as Record<string, unknown>;
    if (record.is_active === false || record.is_active === 0) continue;

    const id = numberOrNull(record.id);
    const panels = numberOrNull(record.panels);
    const cost = numberOrNull(record.cost);
    if (id == null || panels == null || panels <= 0 || cost == null || cost <= 0) continue;

    const battery = numberOrNull(record.battery);
    const savings = numberOrNull(record.savings);
    const systemOutput = numberOrNull(record.system_output);
    const displayOrder = numberOrNull(record.display_order);

    options.push({
      id,
      panels: Math.round(panels),
      batteryKwh: battery == null || battery < 0 ? 0 : battery,
      cost: Math.round(cost),
      annualBenefit: Math.max(0, Math.round(savings ?? 0)),
      systemOutputW: systemOutput != null && systemOutput > 0 ? Math.round(systemOutput) : null,
      displayOrder: displayOrder == null ? null : Math.round(displayOrder),
    });
  }

  return options;
}

export function uniqueExamplePanelCounts(options: readonly ExampleSystemOption[]): number[] {
  return [...new Set(options.map((option) => option.panels))].sort((a, b) => a - b);
}

/** Battery sizes sold with this panel count, smallest first. */
export function batteriesForPanelCount(options: readonly ExampleSystemOption[], panels: number): number[] {
  return [...new Set(options.filter((option) => option.panels === panels).map((option) => option.batteryKwh))].sort(
    (a, b) => a - b,
  );
}

/**
 * The homepage starting package: display order 1 when that row is priced,
 * otherwise the package closest to the illustrative 12-panel / 11.52 kWh example.
 */
export function pickAiExampleOption(options: readonly ExampleSystemOption[]): ExampleSystemOption | null {
  const featured = options
    .filter((option) => option.displayOrder === 1)
    .sort((a, b) => a.id - b.id);
  if (featured.length > 0) return featured[0];
  return snapExampleOption(options, DEFAULT_INPUTS.panels, AI_PICK_BATTERY_KWH);
}

/**
 * Land on a real package. Panel count snaps to the nearest sold count, then
 * battery snaps to the nearest size sold with that count. Ties keep the lowest id.
 */
export function snapExampleOption(
  options: readonly ExampleSystemOption[],
  panels: number,
  batteryKwh: number,
): ExampleSystemOption | null {
  if (options.length === 0 || !Number.isFinite(panels)) return null;

  const panelTarget = uniqueExamplePanelCounts(options).reduce((best, count) =>
    Math.abs(count - panels) < Math.abs(best - panels) ? count : best,
  );
  const pool = options.filter((option) => option.panels === panelTarget);
  const candidates = pool.length > 0 ? pool : [...options];

  return candidates.reduce((best, option) => {
    const dist = Math.abs(option.batteryKwh - batteryKwh);
    const bestDist = Math.abs(best.batteryKwh - batteryKwh);
    if (dist < bestDist) return option;
    if (dist === bestDist && option.id < best.id) return option;
    return best;
  });
}
